import React from "react";
import { useParams } from "react-router-dom";
import {
    Button,
    Typography,
    Box,
    Skeleton,
    Card,
    CardContent,
    Paper,
    Divider,
} from "@mui/material";
import { LocalShipping, Print } from "@mui/icons-material";

import { useLazyGetShipmentDetailByVehicleIdQuery } from "store/services/shipmentLoadingApi";
import { useAppMeta, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import { generateLoadingPrint } from "utils/generateLoadingPrint";

const LoadingShipmentDetailView = () => {
    const { id: vehicleId } = useParams();
    const [isPrinting, setIsPrinting] = React.useState(false);

    const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();

    const [fetchShipmentDetails, { data: response, isLoading, isFetching }] =
        useLazyGetShipmentDetailByVehicleIdQuery();

    React.useEffect(() => {
        if (!vehicleId) return;
        fetchShipmentDetails({ vehicleId, page: currentPage, limit: perPage });
    }, [vehicleId, currentPage, perPage, fetchShipmentDetails]);

    const data = response?.data;

    const handlePrintAll = async () => {
        if (!vehicleId) return;
        try {
            setIsPrinting(true);
            const fullResponse = await fetchShipmentDetails({
                vehicleId,
                page: 1,
                limit: 1000000,
            }).unwrap();

            generateLoadingPrint(fullResponse?.data ?? fullResponse);
        } catch (err) {
            console.error("Failed to fetch full print data:", err);
            alert("Failed to load print data. Try again.");
        } finally {
            setIsPrinting(false);
        }
    };

    const breadcrumbs = [
        { title: "Vehicles", path: "/app/crm/vehicle/list" },
        { title: "Loading Status", path: "/app/crm/shipment-loading/loading-by-vehicles" },
        { title: data?.vehicle_id || "Loading Details" },
    ];

    const HeaderActions = () => (
        <Button
            onClick={handlePrintAll}
            startIcon={<Print />}
            variant="contained"
            size="small"
            disabled={isPrinting}
        >
            {isPrinting ? "Preparing..." : "Print All"}
        </Button>
    );

    const columns = [
        { name: "Shipment ID", selector: (row) => row.shipmentId, sortable: false },
        { name: "Loading ID", selector: (row) => row.loadingId, sortable: false },
        { name: "Sender Name", selector: (row) => row.senderName || "N/A", sortable: false },
        { name: "Receiver Name", selector: (row) => row.receiverName || "N/A", sortable: false },
        { name: "Receiver Phone", selector: (row) => row.receiverPhone || "N/A", sortable: false },
        { name: "Weight (kg)", selector: (row) => `${row.weight} kg`, sortable: false },
        { name: "Loading Date", selector: (row) => new Date(row.loadingTime).toLocaleDateString(), sortable: false },
    ];

    useAppMeta(`Loading Details • ${data?.vehicle_id || ""}`);

    if (isLoading) {
        return (
            <BreadcrumbLayout breadcrumbs={breadcrumbs} isBusy={true}>
                <BreadcrumbLayout.Paper>
                    <Box className="p-6 space-y-6">
                        <Skeleton variant="rectangular" height={100} className="rounded-lg" />
                        <Skeleton variant="rectangular" height={300} className="rounded-lg" />
                    </Box>
                </BreadcrumbLayout.Paper>
            </BreadcrumbLayout>
        );
    }

    return (
        <BreadcrumbLayout breadcrumbs={breadcrumbs} headerActions={<HeaderActions />}>
            <BreadcrumbLayout.Paper>
                <div className="p-6 space-y-3">
                    <Card className="border dark:border-gray-700">
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="bg-blue-100 dark:bg-blue-900 rounded-lg p-3">
                                    <LocalShipping className="w-8 h-8 text-blue-600 dark:text-blue-300" />
                                </div>
                                <div>
                                    <Typography variant="h4" className="font-bold">
                                        {data?.vehicle_id}
                                    </Typography>
                                    <Typography variant="body2" className="text-gray-600 dark:text-gray-400">
                                        Vehicle Loading Details
                                    </Typography>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="border dark:border-gray-700">
                            <CardContent className="p-5" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">Total Loadings</Typography>
                                <Typography variant="h4">{data?.totalLoadings}</Typography>
                            </CardContent>
                        </Card>

                        <Card className="border dark:border-gray-700">
                            <CardContent className="p-3" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">Total Shipments</Typography>
                                <Typography variant="h4">{data?.totalShipments}</Typography>
                            </CardContent>
                        </Card>

                        <Card className="border dark:border-gray-700">
                            <CardContent className="p-3" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2">Total Weight</Typography>
                                <Typography variant="h4">{data?.totalWeight} kg</Typography>
                            </CardContent>
                        </Card>
                    </div>

                    <Paper sx={{ p: 2, mt: 2 }}>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                            Shipments by Loading
                        </Typography>

                        <Divider sx={{ mb: 2 }} />

                        <AppTable
                            columns={columns}
                            data={data?.shipments ?? []}
                            paginationServer
                            progressPending={isLoading || isFetching}
                            paginationTotalRows={response?.totalItems ?? 0}
                            paginationPerPage={perPage}
                            paginationDefaultPage={currentPage}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerRowsChange}
                        />
                    </Paper>
                </div>
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

export default LoadingShipmentDetailView;